import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { APP_PAGES } from '../../routes/constants';
import { useDoBoxRunViewStore } from '../../store/DoBoxRunView';
import { DOBOX_RUN_VIEW_PARAM_VALUE, URL_PARAMS } from '../DataFetch/constants';

export interface OpenDoBoxRunViewOptions {
  fullScreen?: boolean;
  taskGroupId?: string;
  /** Opens this run (a secondary one, or a past one) instead of the current one. */
  runId?: string;
  /** Opens the ticket on its home screen, where every run is listed, instead of one run's screen. */
  showRunList?: boolean;
}

/**
 * The entry point for jumping straight to an entry's agent-presence run view from
 * anywhere in the app (e.g. a "Watch" CTA on a list/board card, or
 * `ExecutorCodeDelegationCard`), not just from inside the already-open DoBox details
 * modal. A caller already inside that modal (the header's own status chip) doesn't need
 * this — it can call `useDoBoxRunViewStore.getState().requestRunView` directly, since the
 * modal is open by construction there.
 *
 * On the DoBox page, opens the entry's modal in place through the page's own
 * `pageOpenHandler` (registered imperatively by `DoBox.tsx` — its `handleDirectOpen`,
 * the same mechanism a normal card click uses) instead of writing `?doboxId=`/
 * `?taskGroupId=` via `setSearchParams`. That URL round-trip used to race
 * `useDoBoxTaskGroup`'s mount-only effect: writing a `taskGroupId` that didn't match the
 * page's already-active group tripped `useDoBoxModals`'s deep-link mismatch guard and
 * silently kept the modal closed. Going through `pageOpenHandler` never touches
 * `taskGroupId` at all, so that guard never engages — the same reason a plain card click
 * (which also never writes `taskGroupId`) never hit it either. Off the DoBox page, still
 * navigates there with `?doboxId=`/`?taskGroupId=` set, since the page needs those to
 * resolve/switch the active group on mount.
 *
 * Either way, also sets `?runView=1` (or `=full`) so a reload or a shared link reopens
 * the run view — `InboxEntryDetailsModal` consumes and clears that param on mount. With
 * `showRunList` the ticket opens on its home screen and no run view is requested at all.
 */
export function useOpenDoBoxRunView(): (
  entryId: string,
  options?: OpenDoBoxRunViewOptions,
) => void {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  const openRunList = useCallback(
    (entryId: string, taskGroupId: string | undefined) => {
      useDoBoxRunViewStore.getState().closeRunView(entryId);
      if (location.pathname === APP_PAGES.DOBOX) {
        const { pageOpenHandler } = useDoBoxRunViewStore.getState();
        if (pageOpenHandler) {
          pageOpenHandler(entryId);
          return;
        }
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set(URL_PARAMS.DOBOX_ID, entryId);
            return newParams;
          },
          { replace: true },
        );
        return;
      }
      const newParams = new URLSearchParams();
      newParams.set(URL_PARAMS.DOBOX_ID, entryId);
      if (taskGroupId) {
        newParams.set(URL_PARAMS.TASK_GROUP_ID, taskGroupId);
      }
      navigate(`${APP_PAGES.DOBOX}?${newParams.toString()}`);
    },
    [location.pathname, navigate, setSearchParams],
  );

  return useCallback(
    (entryId: string, options?: OpenDoBoxRunViewOptions) => {
      const isOnDoBoxPage = location.pathname === APP_PAGES.DOBOX;
      if (options?.showRunList) {
        openRunList(entryId, options.taskGroupId);
        return;
      }
      const runViewParamValue = options?.fullScreen
        ? DOBOX_RUN_VIEW_PARAM_VALUE.FULL_SCREEN
        : DOBOX_RUN_VIEW_PARAM_VALUE.OPEN;

      if (isOnDoBoxPage) {
        const { pageOpenHandler } = useDoBoxRunViewStore.getState();

        if (pageOpenHandler) {
          // doboxId and runView MUST land in the same setSearchParams update —
          // react-router's functional updater resolves against the current
          // render's params, so a separate setSearchParams call right after this
          // one would start from stale prev and drop doboxId.
          pageOpenHandler(entryId, {
            [URL_PARAMS.RUN_VIEW]: runViewParamValue,
          });
        } else {
          // Defensive fallback only — pageOpenHandler should always be registered
          // while this page is mounted. Never also writes taskGroupId: that's the
          // write this hook exists to avoid.
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set(URL_PARAMS.DOBOX_ID, entryId);
              newParams.set(URL_PARAMS.RUN_VIEW, runViewParamValue);
              return newParams;
            },
            { replace: true },
          );
        }
      } else {
        const newParams = new URLSearchParams();
        newParams.set(URL_PARAMS.DOBOX_ID, entryId);
        if (options?.taskGroupId) {
          newParams.set(URL_PARAMS.TASK_GROUP_ID, options.taskGroupId);
        }
        newParams.set(URL_PARAMS.RUN_VIEW, runViewParamValue);
        navigate(`${APP_PAGES.DOBOX}?${newParams.toString()}`);
      }

      useDoBoxRunViewStore.getState().requestRunView(entryId, {
        fullScreen: options?.fullScreen,
        runId: options?.runId,
      });
    },
    [location.pathname, navigate, setSearchParams, openRunList],
  );
}
